import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LockResetIcon from '@mui/icons-material/LockReset';
import BlockIcon from '@mui/icons-material/Block';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { useAuth } from '../../contexts/AuthContext';

interface DeviceFtpCredentialTabProps {
  deviceId: string;
}

interface CredentialStatus {
  exists: boolean;
  username?: string;
  dateCreated?: string;
  rotatedAt?: string;
  lastSyncedAt?: string | null;
  synced?: boolean;
}

// Same rule as the API: many cameras cap the FTP username (Reolink at 31
// characters), which a 36-character device ID doesn't fit.
const MAX_USERNAME_LENGTH = 31;
const USERNAME_PATTERN = /^[a-z0-9][a-z0-9._-]{2,30}$/;

interface RevealedPassword {
  username: string;
  password: string;
  synced: boolean;
  message: string;
}

const DeviceFtpCredentialTab: React.FC<DeviceFtpCredentialTabProps> = ({ deviceId }) => {
  const auth = useAuth();
  const accessToken = auth.tokens?.accessToken;
  const authHeader = { Authorization: `Bearer ${accessToken}` };

  const [status, setStatus] = useState<CredentialStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [revealed, setRevealed] = useState<RevealedPassword | null>(null);
  const [rotateConfirmOpen, setRotateConfirmOpen] = useState(false);
  const [disableConfirmOpen, setDisableConfirmOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [renameError, setRenameError] = useState<string | null>(null);
  const [renaming, setRenaming] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`/api/devices/${deviceId}/ftp-credential`, { headers: authHeader });
      if (!res.ok) throw new Error('Failed to load FTP credential status');
      const data = await res.json();
      setStatus(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load FTP credential status');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, accessToken]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const handleCreate = async () => {
    setActionLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/devices/${deviceId}/ftp-credential`, {
        method: 'POST',
        headers: authHeader,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create credential');
      setRevealed(data);
      await loadStatus();
    } catch (err: any) {
      setError(err.message || 'Failed to create credential');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRotate = async () => {
    setActionLoading(true);
    setError(null);
    setRotateConfirmOpen(false);
    try {
      const res = await fetch(`/api/devices/${deviceId}/ftp-credential/rotate`, {
        method: 'POST',
        headers: authHeader,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to rotate credential');
      setRevealed(data);
      await loadStatus();
    } catch (err: any) {
      setError(err.message || 'Failed to rotate credential');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDisable = async () => {
    setActionLoading(true);
    setError(null);
    setDisableConfirmOpen(false);
    try {
      const res = await fetch(`/api/devices/${deviceId}/ftp-credential/disable`, {
        method: 'POST',
        headers: authHeader,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to disable credential');
      if (!data.synced) {
        setError(data.message);
      }
      await loadStatus();
    } catch (err: any) {
      setError(err.message || 'Failed to disable credential');
    } finally {
      setActionLoading(false);
    }
  };

  const openRename = () => {
    const current = revealed?.username ?? status?.username ?? '';
    // Suggest something short when it's still the device ID
    setRenameValue(current === deviceId ? `cam-${deviceId.slice(0, 8)}` : current);
    setRenameError(null);
    setRenameOpen(true);
  };

  const renameValid = renameValue === deviceId || USERNAME_PATTERN.test(renameValue);

  const handleRename = async () => {
    setRenaming(true);
    setRenameError(null);
    try {
      const res = await fetch(`/api/devices/${deviceId}/ftp-credential/username`, {
        method: 'PUT',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: renameValue }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to change username');
      setRenameOpen(false);
      // Keep the one-time password dialog showing the login the camera needs
      setRevealed((r) => (r ? { ...r, username: data.username } : r));
      if (data.message && data.message !== 'Username changed. The password is unchanged.') {
        setError(data.message);
      }
      await loadStatus();
    } catch (err: any) {
      setRenameError(err.message || 'Failed to change username');
    } finally {
      setRenaming(false);
    }
  };

  const copyPassword = () => {
    if (!revealed) return;
    navigator.clipboard.writeText(revealed.password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const closeRevealDialog = () => {
    setRevealed(null);
    setCopied(false);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3 }}>
        FTP Credential
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 3 }}>
        {!status?.exists ? (
          <Stack spacing={2} alignItems="flex-start">
            <Typography color="text.secondary">
              No FTP credential configured for this device yet.
            </Typography>
            <Button
              variant="contained"
              startIcon={actionLoading ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
              disabled={actionLoading}
              onClick={handleCreate}
            >
              Create Credential
            </Button>
          </Stack>
        ) : (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="subtitle1">{status.username}</Typography>
              <Chip
                label={status.synced ? 'Synced' : 'Not synced'}
                color={status.synced ? 'success' : 'warning'}
                size="small"
              />
            </Stack>

            {!status.synced && (
              <Alert severity="warning">
                This credential has not synced to the edge FTP server. It will not work for login until sync succeeds.
              </Alert>
            )}

            <Stack direction="row" spacing={4}>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Created
                </Typography>
                <Typography variant="body2">
                  {status.dateCreated ? new Date(status.dateCreated).toLocaleString() : '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Last Rotated
                </Typography>
                <Typography variant="body2">
                  {status.rotatedAt ? new Date(status.rotatedAt).toLocaleString() : '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Last Synced
                </Typography>
                <Typography variant="body2">
                  {status.lastSyncedAt ? new Date(status.lastSyncedAt).toLocaleString() : 'Never'}
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                disabled={actionLoading}
                onClick={openRename}
              >
                Change Username
              </Button>
              <Button
                variant="outlined"
                color="warning"
                startIcon={<LockResetIcon />}
                disabled={actionLoading}
                onClick={() => setRotateConfirmOpen(true)}
              >
                Rotate Password
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<BlockIcon />}
                disabled={actionLoading}
                onClick={() => setDisableConfirmOpen(true)}
              >
                Disable Credential
              </Button>
            </Stack>
          </Stack>
        )}
      </Paper>

      {/* One-time password reveal dialog */}
      <Dialog open={!!revealed} onClose={closeRevealDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Save this password now</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This password is shown once and cannot be retrieved again. Copy it into the camera's FTP
            configuration now — if it's lost, you'll need to rotate again to get a new one.
          </Alert>
          <Stack spacing={2}>
            <TextField
              label="Username"
              value={revealed?.username || ''}
              InputProps={{ readOnly: true }}
              helperText={
                <>
                  Camera won't take a username this long?{' '}
                  <Button size="small" onClick={openRename} sx={{ p: 0, minWidth: 0, verticalAlign: 'baseline', textTransform: 'none' }}>
                    Change it
                  </Button>
                  {' '}— the password stays the same.
                </>
              }
              fullWidth
            />
            <TextField
              label="Password"
              value={revealed?.password || ''}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <IconButton onClick={copyPassword} edge="end" size="small">
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                ),
              }}
              fullWidth
            />
            {copied && <Alert severity="success">Copied to clipboard.</Alert>}
            {revealed && !revealed.synced && (
              <Alert severity="warning">{revealed.message}</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRevealDialog} variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change username */}
      <Dialog open={renameOpen} onClose={() => !renaming && setRenameOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Change FTP Username</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The password stays the same, and uploads are still credited to this device. Update the camera's
            FTP username afterward — the old username stops working once this is saved.
          </Typography>
          {renameError && <Alert severity="error" sx={{ mb: 2 }}>{renameError}</Alert>}
          <TextField
            label="Username"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value.toLowerCase().trim())}
            error={!!renameValue && !renameValid}
            helperText={
              renameValue && !renameValid
                ? `3-${MAX_USERNAME_LENGTH} characters: lowercase letters, digits, '.', '_' or '-'`
                : `${renameValue.length}/${MAX_USERNAME_LENGTH} characters`
            }
            inputProps={{ maxLength: 36 }}
            autoFocus
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameOpen(false)} disabled={renaming}>Cancel</Button>
          <Button onClick={handleRename} variant="contained" disabled={renaming || !renameValid}>
            {renaming ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rotate confirmation */}
      <Dialog open={rotateConfirmOpen} onClose={() => setRotateConfirmOpen(false)}>
        <DialogTitle>Rotate FTP Password</DialogTitle>
        <DialogContent>
          <Typography>
            This generates a new password and invalidates the old one once synced. You'll need to update
            the camera's FTP configuration with the new password afterward, or it will stop uploading.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRotateConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleRotate} color="warning" variant="contained">
            Rotate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Disable confirmation */}
      <Dialog open={disableConfirmOpen} onClose={() => setDisableConfirmOpen(false)}>
        <DialogTitle>Disable FTP Credential</DialogTitle>
        <DialogContent>
          <Typography>
            This device will no longer be able to upload files via FTP once synced. You can create a new
            credential for it again later if needed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisableConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDisable} color="error" variant="contained">
            Disable
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeviceFtpCredentialTab;
